import { Assert } from '@cotera/utilities';
import { Shorthand, shorthandToTy } from '../ty/ty';
import {
  TypeConstraintItem,
  TypeConstraint,
  RecordTypeConstraint,
  ArrayTypeConstraint,
  StructTypeConstraint,
  ConcreteTypeConstraint,
} from './constraints';
import { Ty } from '../ty';
import _ from 'lodash';
import { Freeze } from '../utils';
import { implementsTypeConstraint } from '../type-checker/implements';

abstract class ConstraintBuilder {
  private _meta: TypeConstraintItem['meta'] = {};
  private _optional: boolean = false;
  abstract asConstraint(): TypeConstraint;

  get meta() {
    return this._meta;
  }

  get optional() {
    return this._optional;
  }

  withMeta(meta: TypeConstraintItem['meta']) {
    this._meta = meta;
    return this;
  }

  withDescription(value: string) {
    this.withMeta({ description: value });
    return this;
  }

  containsTy(ty: Ty.Shorthand): boolean {
    return implementsTypeConstraint(ty, this.asConstraint()).isOk();
  }

  isOptional(): this {
    this._optional = true;

    return this;
  }
}

class RecordBuilder extends ConstraintBuilder implements RecordTypeConstraint {
  readonly t = 'record';
  readonly k = 'constraint';

  override asConstraint(): TypeConstraint {
    return [this];
  }

  constructor(readonly allowed: TypeConstraint) {
    super();
  }
}

class ArrayBuilder extends ConstraintBuilder implements ArrayTypeConstraint {
  readonly t = 'array';
  readonly k = 'constraint';

  override asConstraint(): TypeConstraint {
    return [this];
  }

  constructor(readonly allowed: TypeConstraint) {
    super();
  }
}

class StructBuilder extends ConstraintBuilder implements StructTypeConstraint {
  readonly t = 'struct';
  readonly k = 'constraint';

  override asConstraint(): TypeConstraint {
    return [this];
  }

  constructor(
    readonly fields: {
      [name: string]: { ty: TypeConstraint; optional: boolean };
    }
  ) {
    super();
  }
}

class ConcreteTypeBuilder
  extends ConstraintBuilder
  implements ConcreteTypeConstraint
{
  readonly t = 'concrete-type';
  readonly k = 'constraint';
  readonly ty: Ty.ExtendedAttributeType;

  override asConstraint(): TypeConstraint {
    return [this];
  }

  constructor(ty: Shorthand) {
    super();
    this.ty = shorthandToTy(ty);
  }
}

export const record = (allowed: TypeConstraint) => {
  return new RecordBuilder(allowed);
};

export const struct = (fields: {
  [name: string]: TypeConstraint | { ty: TypeConstraint; optional?: boolean };
}) => {
  return new StructBuilder(
    _.mapValues(fields, (ty) =>
      ty === 'any' || Freeze.isReadonlyArray(ty)
        ? { ty, optional: false }
        : { ...ty, optional: ty.optional ?? false }
    )
  );
};

export const array = (allowed: TypeConstraint) => {
  return new ArrayBuilder(allowed);
};

export const any = (): TypeConstraint => {
  return 'any';
};

export const ty = (ty: Shorthand) => {
  return new ConcreteTypeBuilder(ty);
};

export const number = () => {
  return [ty('int'), ty('float')];
};

export const build = (
  ...constraints: (TypeConstraintItem | 'any')[]
): TypeConstraint => {
  return constraints as TypeConstraint;
};

export const merge = (...constraints: TypeConstraint[]): TypeConstraint => {
  if (constraints.length === 0) {
    return 'any';
  }

  if (constraints.some((c) => c === 'any')) {
    return 'any';
  }

  return constraints
    .map((c) => {
      Assert.assert(c !== 'any');

      return c;
    })
    .flat();
};

export const flatten = (
  constraints: TypeConstraint
): TypeConstraintItem | 'any' => {
  if (constraints.length === 0) {
    return 'any';
  }

  Assert.assert(constraints !== 'any');

  const types = new Set(
    constraints
      .map((c) => {
        return c.t;
      })
      .flat()
  );

  if (types.size > 1) {
    throw new Error('Cannot merge different types');
  }

  const type = [...types.values()][0];
  Assert.assert(type !== undefined);

  switch (type) {
    case 'record':
      return {
        t: 'record',
        k: 'constraint',
        allowed: constraints
          .map((c) => {
            Assert.assert(c.t === 'record');
            return c.allowed;
          })
          .flat() as TypeConstraint,
      };
    case 'concrete-type': {
      const firstTy = constraints.at(0)!;
      Assert.assert(firstTy.t === 'concrete-type');
      return {
        t: 'concrete-type',
        k: 'constraint',
        ty: firstTy.ty,
      };
    }
    case 'array': {
      return {
        t: 'array',
        k: 'constraint',
        allowed: constraints
          .map((c) => {
            Assert.assert(c.t === 'array');
            return c.allowed;
          })
          .flat() as TypeConstraint,
      };
    }
    case 'struct': {
      return {
        t: 'struct',
        k: 'constraint',
        fields: Object.fromEntries(
          constraints
            .map((c) => {
              Assert.assert(c.t === 'struct');
              return c.fields;
            })
            .flatMap(Object.entries)
        ),
      };
    }
    default:
      return Assert.unreachable(type);
  }
};
